import { createHttpService } from '../../ApiService';
import { normalizePaginatedResponse } from '../../__shared/paginationUtils';
import {
  TStudentDetailsProfileResponse,
  TStudentDetailsAttendanceParams,
  TStudentDetailsAttendanceResponse,
  TStudentDetailsFeesResponse,
  TStudentDetailsAssessmentsParams,
  TStudentDetailsAssessmentsResponse,
  TStudentDetailsDocumentsParams,
  TStudentDetailsDocumentsResponse,
  TFeeUpdateRequest,
} from './studentDetails.interfaces';
import type {
  TStudentAttendanceRecord,
  TStudentAssessmentRecord,
} from './studentDetails.interfaces';
import type { TDocument } from '../../documents/documents.interfaces';
import { mockStudentDetails } from '../../../__mocks__/students/studentDetailsMock';
import { mockStudentDocuments } from '../../../__mocks__/students/studentDocumentsMock';
import { mockStudentDetailsAttendance } from '../../../__mocks__/students/studentDetails/studentDetailsAttendance.mock';
import { mockStudentDetailsFees } from '../../../__mocks__/students/studentDetails/studentDetailsFees.mock';
import { mockStudentDetailsAssessments } from '../../../__mocks__/students/studentDetails/studentDetailsAssessments.mock';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useStudentDetailsApi = () => {
  const httpAPI = createHttpService();
  const basePath = '';
  const withMockData = isAppWithMock();

  const getStudentProfile = (
    studentId: string
  ): Promise<TStudentDetailsProfileResponse> => {
    if (withMockData) {
      const studentData = mockStudentDetails;
      const guardians = studentData.guardianDetails || [];
      const primaryGuardians = guardians.slice(0, 2);
      const extraGuardians = guardians.slice(2);

      return Promise.resolve({
        student: studentData,
        guardians: primaryGuardians,
        extraGuardians,
      });
    }
    // TODO: Naman - Add enrollmentHistory, previousSchool, enrollmentDocuments to student profile API
    return httpAPI.students
      .get<TStudentDetailsProfileResponse>(`${basePath}/${studentId}/profile`)
      .then((response) => {
        return {
          student: response.student,
          guardians: response.student.guardianDetails?.length ?
            response.student.guardianDetails :
            response.guardians?.length ? response.guardians :
              [],
          extraGuardians: response.extraGuardians,
        }
      })
      .catch((error) => {
        console.error('Error fetching student profile:', error);
        throw error;
      });
  };

  const getStudentAttendance = (
    studentId: string,
    params: TStudentDetailsAttendanceParams
  ): Promise<TStudentDetailsAttendanceResponse> => {
    if (withMockData) {
      let filteredData = [...mockStudentDetailsAttendance];

      if (params.fromDate) {
        filteredData = filteredData.filter((a) => a.date >= params.fromDate!);
      }
      if (params.toDate) {
        filteredData = filteredData.filter((a) => a.date <= params.toDate!);
      }

      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      return Promise.resolve(
        normalizePaginatedResponse<TStudentAttendanceRecord>(
          { data: paginatedData, count: filteredData.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 10,
        ...(params.fromDate && { fromDate: params.fromDate }),
        ...(params.toDate && { toDate: params.toDate }),
      },
    };

    return httpAPI.students
      .get<Record<string, unknown>>(
        `${basePath}/${studentId}/attendance`,
        requestObj
      )
      .then((res) =>
        normalizePaginatedResponse<TStudentAttendanceRecord>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      )
      .catch((error) => {
        console.error('Error fetching student attendance:', error);
        throw error;
      });
  };

  const getStudentFees = (
    studentId: string
  ): Promise<TStudentDetailsFeesResponse> => {
    if (withMockData) {
      return Promise.resolve(mockStudentDetailsFees);
    }

    return httpAPI.students
      .get<TStudentDetailsFeesResponse>(`${basePath}/${studentId}/fees`)
      .then((response) => response)
      .catch((error) => {
        console.error('Error fetching student fees:', error);
        throw error;
      });
  };

  const updateStudentFees = (
    studentId: string,
    fees: TFeeUpdateRequest
  ): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.students
      .put<void>(`${basePath}/${studentId}/fees`, fees)
      .then(() => {
        // Success
      })
      .catch((error) => {
        console.error('Error updating student fees:', error);
        throw error;
      });
  };

  const getStudentAssessments = (
    studentId: string,
    params: TStudentDetailsAssessmentsParams
  ): Promise<TStudentDetailsAssessmentsResponse> => {
    if (withMockData) {
      let filteredData = [...mockStudentDetailsAssessments];

      if (params.subject) {
        filteredData = filteredData.filter((a) => a.subject === params.subject);
      }
      if (params.fromDate) {
        filteredData = filteredData.filter((a) => a.date >= params.fromDate!);
      }
      if (params.toDate) {
        filteredData = filteredData.filter((a) => a.date <= params.toDate!);
      }

      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      return Promise.resolve(
        normalizePaginatedResponse<TStudentAssessmentRecord>(
          { data: paginatedData, count: filteredData.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 10,
        ...(params.subject && { subject: params.subject }),
        ...(params.fromDate && { fromDate: params.fromDate }),
        ...(params.toDate && { toDate: params.toDate }),
      },
    };

    return httpAPI.students
      .get<Record<string, unknown>>(
        `${basePath}/${studentId}/assessments`,
        requestObj
      )
      .then((res) =>
        normalizePaginatedResponse<TStudentAssessmentRecord>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      )
      .catch((error) => {
        console.error('Error fetching student assessments:', error);
        throw error;
      });
  };

  const getStudentDocuments = (
    studentId: string,
    params: TStudentDetailsDocumentsParams
  ): Promise<TStudentDetailsDocumentsResponse> => {
    if (withMockData) {
      let filteredData = [...mockStudentDocuments];

      if (params.category) {
        filteredData = filteredData.filter(
          (d) => d.documentType === params.category
        );
      }
      if (params.type) {
        filteredData = filteredData.filter((d) => d.documentType === params.type);
      }

      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      return Promise.resolve(
        normalizePaginatedResponse<TDocument>(
          { data: paginatedData, count: filteredData.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 10,
        ...(params.category && { category: params.category }),
        ...(params.type && { type: params.type }),
      },
    };

    return httpAPI.students
      .get<Record<string, unknown>>(
        `${basePath}/${studentId}/documents`,
        requestObj
      )
      .then((res) =>
        normalizePaginatedResponse<TDocument>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      )
      .catch((error) => {
        console.error('Error fetching student documents:', error);
        throw error;
      });
  };

  return {
    getStudentProfile,
    getStudentAttendance,
    getStudentFees,
    updateStudentFees,
    getStudentAssessments,
    getStudentDocuments,
  };
};
